/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import type { Spread } from "lexical";

import {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedTextNode,
  $applyNodeReplacement,
  TextNode,
} from "lexical";

export type SerializedCustomFieldNode = Spread<
  {
    customFieldValue: string;
    customFieldKey: string;
    hasArtificialValue: boolean;
    highlightUnknownCustomFields: boolean;
    openEditCustomFieldModal:
      | undefined
      | ((key: string, value: string) => void);
    type: "custom-field";
    version: 1;
  },
  SerializedTextNode
>;

function humanize(str: string): string {
  return str
    .replace(/_/g, " ")
    .replace(/[{}]/g, "")
    .replace(/(?: |\b)(\w)/g, (key) => key.toUpperCase());
}

const customFieldStyle = `
  border-radius: 0.25rem;
  overflow: hidden;
  white-space: nowrap;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  opacity: 0;
`;
export class CustomFieldNode extends TextNode {
  __customFieldValue: string;
  __customFieldKey: string;
  __hasArtificialValue: boolean;
  __highlightUnknownCustomFields: boolean;
  __openEditCustomFieldModal:
    | undefined
    | ((key: string, value: string) => void);

  static getType(): string {
    return "custom-field";
  }

  static clone(node: CustomFieldNode): CustomFieldNode {
    return new CustomFieldNode(
      node.__customFieldValue,
      node.__customFieldKey,
      node.__highlightUnknownCustomFields,
      node.__openEditCustomFieldModal,
      node.__hasArtificialValue,
      node.__key
    );
  }

  static importJSON(
    serializedNode: SerializedCustomFieldNode
  ): CustomFieldNode {
    const node = $createCustomFieldNode(
      serializedNode.customFieldValue,
      serializedNode.customFieldKey,
      serializedNode.highlightUnknownCustomFields,
      serializedNode.openEditCustomFieldModal,
      serializedNode.hasArtificialValue
    );
    node.setFormat(serializedNode.format);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    node.setStyle(serializedNode.style);
    return node;
  }

  constructor(
    customFieldValue: string | undefined,
    customFieldKey: string,
    highlightUnknownCustomFields: boolean,
    openEditCustomFieldModal?: (key: string, value: string) => void,
    hasArtificialValue?: boolean,
    key?: NodeKey
  ) {
    let displayText = customFieldValue;

    if (!customFieldValue && !highlightUnknownCustomFields) {
      displayText = humanize(customFieldKey);
    }

    const actualText = customFieldKey;

    super(actualText, key);

    this.__highlightUnknownCustomFields = highlightUnknownCustomFields;
    this.__customFieldValue = displayText || customFieldKey;
    this.__customFieldKey = actualText;
    this.__hasArtificialValue =
      customFieldValue === undefined || !!hasArtificialValue;
    this.__openEditCustomFieldModal = openEditCustomFieldModal;
  }

  exportJSON(): SerializedCustomFieldNode {
    return {
      ...super.exportJSON(),
      customFieldValue: this.__customFieldValue,
      customFieldKey: this.__customFieldKey,
      hasArtificialValue: this.__hasArtificialValue,
      highlightUnknownCustomFields: this.__highlightUnknownCustomFields,
      openEditCustomFieldModal: this.__openEditCustomFieldModal,
      type: "custom-field",
      version: 1,
    };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.style.cssText = customFieldStyle;
    dom.className = "custom-field";
    const element = document.createElement("span");
    element.style.cssText = "position: relative; display: inline-block;";

    if (typeof this.__openEditCustomFieldModal !== "undefined") {
      element.style.cssText = `${element.style.cssText} cursor: pointer;`;
      element.addEventListener("click", () => {
        this.__openEditCustomFieldModal?.(
          this.__customFieldKey.replace(/[{}]/g, ""),
          this.__customFieldValue
        );
      });
    }

    const textAbove = document.createElement("span");
    textAbove.textContent = this.__customFieldValue;
    textAbove.style.cssText = `
      background-color: var(--chakra-colors-${
        this.__hasArtificialValue ? "gray" : "green"
      }-400);
      color: var(--chakra-colors-white);
      padding-left: 0.25rem;
      padding-right: 0.25rem;
      border-radius: 0.25rem;
      pointer-events: none;
      margin-top: 1px;
      margin-bottom: 1px;
      user-select: none;
      width: max-content;
      display: inline-block;
    `;
    textAbove.appendChild(dom);
    element.appendChild(textAbove);
    return element;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("span");
    element.setAttribute("data-is-custom-field", "true");
    element.setAttribute("data-custom-field-key", this.__customFieldKey);
    element.setAttribute("data-custom-field-value", this.__customFieldValue);
    element.setAttribute(
      "data-has-artificial-value",
      this.__hasArtificialValue.toString()
    );
    element.setAttribute(
      "data-highlight-unknown-custom-fields",
      this.__highlightUnknownCustomFields.toString()
    );
    if (typeof this.__openEditCustomFieldModal !== "undefined") {
      element.onclick = (_ev: MouseEvent) => this.__openEditCustomFieldModal;
    }

    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute("data-is-custom-field")) {
          return null;
        }
        return {
          conversion: convertCustomFieldElement,
          priority: 1,
        };
      },
    };
  }

  isTextEntity(): true {
    return true;
  }
}

function convertCustomFieldElement(
  domNode: HTMLElement
): DOMConversionOutput | null {
  const customFieldKey = domNode.getAttribute("data-custom-field-key");
  const customFieldValue = domNode.getAttribute("data-custom-field-value");
  const hasArtificialValueAttribute = domNode.getAttribute(
    "data-has-artificial-value"
  );
  const hasHighlightUnknownCustomFieldsAttribute = domNode.getAttribute(
    "data-highlight-unknown-custom-fields"
  );
  const hasArtificialValue =
    hasArtificialValueAttribute === null
      ? undefined
      : Boolean(hasArtificialValueAttribute);
  const highlightUnknownCustomFields = Boolean(
    hasHighlightUnknownCustomFieldsAttribute
  );
  const openEditCustomFieldModal = domNode.onclick!({} as MouseEvent);

  if (customFieldValue !== null && customFieldKey !== null) {
    const node = $createCustomFieldNode(
      customFieldValue,
      customFieldKey,
      highlightUnknownCustomFields,
      openEditCustomFieldModal,
      hasArtificialValue
    );
    return {
      node,
    };
  }

  return null;
}

export function $createCustomFieldNode(
  customFieldValue: string | undefined,
  customFieldKey: string,
  highlightUnknownCustomFields: boolean,
  openEditCustomFieldModal?: (key: string, value: string) => void,
  hasArtificialValue?: boolean
): CustomFieldNode {
  const customFieldNode = new CustomFieldNode(
    customFieldValue,
    customFieldKey,
    highlightUnknownCustomFields,
    openEditCustomFieldModal,
    hasArtificialValue
  );
  customFieldNode.setMode("token").toggleDirectionless();
  return $applyNodeReplacement(customFieldNode);
}

export function $isCustomFieldNode(
  node: LexicalNode | null | undefined
): node is CustomFieldNode {
  return node instanceof CustomFieldNode;
}
